import { C, Explainer, Tick, rise, type ExplainerProps, type SceneDef, type SceneProps } from './engine';

const T = { request: 700, consent: 1700, move: 2700, ghost: 3150, owner: 3450, entry: 3800, kept: 4300 };
const CARD = { x0: 16, x1: 254, y: 74, w: 210 };

function Owner({ x, n, name, initials, active, tone }: { x: number; n: number; name: string; initials: string; active: boolean; tone: 'light' | 'dark' }) {
  const dark = tone === 'dark';
  return (
    <div className="xp-abs xp-row" style={{ left: x, top: 18, width: 210, gap: 10, paddingBottom: 12, borderBottom: '1px solid var(--xp-line)' }}>
      <span
        className="xp-row"
        style={{
          width: 30,
          height: 30,
          borderRadius: 9,
          justifyContent: 'center',
          fontSize: 10.5,
          fontWeight: 700,
          background: active ? (dark ? '#fff' : C.indigo) : 'var(--xp-soft)',
          color: active ? (dark ? C.indigo : '#fff') : 'var(--xp-muted)',
          boxShadow: active ? 'none' : 'inset 0 0 0 1px var(--xp-line)',
          transition: 'background .2s, color .2s',
        }}
      >
        {initials}
      </span>
      <span>
        <span className="xp-cap" style={{ display: 'block', fontSize: 8, color: 'var(--xp-muted)' }}>
          Owner {n}
        </span>
        <span style={{ display: 'block', maxWidth: 168, fontSize: name.length > 20 ? 10.5 : 12.5, lineHeight: 1.2, fontWeight: 700, letterSpacing: '-.01em', color: 'var(--xp-fg)', opacity: active ? 1 : 0.55, transition: 'opacity .25s' }}>{name}</span>
      </span>
    </div>
  );
}

const initials = (name: string) =>
  (name.includes(',') ? name.split(',').pop()! : name)
    .replace(/^\s*the\s+/i, '')
    .replace(/\b\d+\w*\b/g, '')
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

function TransferScene({ t, tone, u }: SceneProps) {
  const HISTORY = u.history.slice(0, 3);
  const at = (ms: number) => t >= ms;
  const dark = tone === 'dark';
  const moved = at(T.move);
  const consent = at(T.consent);
  const newOwner = at(T.owner);
  return (
    <>
      <Owner x={16} n={1} name={u.owner} initials={initials(u.owner)} active={!moved} tone={tone} />
      <Owner x={254} n={2} name={u.owner2} initials={initials(u.owner2)} active={moved} tone={tone} />

      {/* the gap between owners */}
      <svg className="xp-abs" style={{ left: 0, top: 0 }} width={480} height={360}>
        <path d="M228 190 H 252" stroke="var(--xp-line)" strokeWidth="1" strokeDasharray="3 3" />
        <path d="M247 186 L 252 190 L 247 194" fill="none" stroke={dark ? 'rgba(255,255,255,.4)' : '#B9B7D3'} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      {/* Request from the new owner */}
      <div className="xp-abs xp-card xp-a" style={{ ...rise(at(T.request) && !moved, 0, 10), left: CARD.x1, top: CARD.y + 16, width: CARD.w, padding: '12px 13px' }}>
        <div className="xp-cap" style={{ fontSize: 7.5, color: C.muted }}>
          Transfer request
        </div>
        <div style={{ fontSize: 10.5, fontWeight: 600, lineHeight: 1.4, marginTop: 5 }}>{u.owner2} asked to take over {u.serial}</div>
        <div className="xp-row" style={{ gap: 6, marginTop: 9, fontSize: 9.5, color: consent ? C.emerald : C.muted, fontWeight: 600, transition: 'color .2s' }}>
          <span style={{ width: 6, height: 6, borderRadius: 6, background: consent ? C.green : '#CFCDE2', transition: 'background .2s' }} />
          {consent ? `Approved by ${u.owner}` : 'Waiting for the current owner'}
        </div>
      </div>

      {/* Where the record was */}
      <div className="xp-abs xp-row xp-f" style={{ left: CARD.x0, top: CARD.y, width: CARD.w, height: 232, borderRadius: 12, border: '1px dashed var(--xp-line)', justifyContent: 'center', textAlign: 'center', opacity: at(T.ghost) ? 1 : 0 }}>
        <span className="xp-cap" style={{ fontSize: 8.5, color: 'var(--xp-muted)', lineHeight: 1.7 }}>
          Sold on
          <br />
          record moved with consent
        </span>
      </div>

      {/* The unit record */}
      <div
        className="xp-abs xp-card"
        style={{
          left: 0,
          top: 0,
          width: CARD.w,
          zIndex: 4,
          transform: `translate(${moved ? CARD.x1 : CARD.x0}px, ${CARD.y}px)`,
          transition: 'transform .85s cubic-bezier(.65,0,.25,1), box-shadow .4s',
          boxShadow: at(T.move) && !at(T.move + 700) ? '0 0 0 1px #E4E3F0, 0 34px 50px -24px rgba(24,19,67,.55)' : undefined,
        }}
      >
        <div style={{ padding: '12px 13px 10px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '-.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.title}</div>
          <div className="xp-mono" style={{ fontSize: 8.5, color: C.muted, marginTop: 2 }}>
            {u.serial}
          </div>
        </div>
        <div className="xp-row" style={{ justifyContent: 'space-between', padding: '8px 13px', borderTop: `1px solid ${C.line}` }}>
          <span className="xp-cap" style={{ fontSize: 7.5, color: C.muted }}>
            Owner
          </span>
          <span style={{ position: 'relative', height: 15, width: 132, overflow: 'hidden', fontSize: 10.5, fontWeight: 600, textAlign: 'right' }}>
            <span className="xp-a" style={{ position: 'absolute', right: 0, whiteSpace: 'nowrap', transform: newOwner ? 'translateY(-15px)' : 'none', opacity: newOwner ? 0 : 1 }}>
              {u.ownerShort}
            </span>
            <span className="xp-a" style={{ position: 'absolute', right: 0, whiteSpace: 'nowrap', transform: newOwner ? 'none' : 'translateY(15px)', opacity: newOwner ? 1 : 0 }}>
              {u.owner2Short}
            </span>
          </span>
        </div>
        <div className="xp-row" style={{ justifyContent: 'space-between', padding: '8px 13px', borderTop: `1px solid ${C.line}` }}>
          <span className="xp-cap" style={{ fontSize: 7.5, color: C.muted }}>
            Consent
          </span>
          <span className="xp-row" style={{ gap: 5, fontSize: 10, fontWeight: 600, color: consent ? C.emerald : '#B3B1CC', transition: 'color .2s' }}>
            <span className="xp-row" style={{ width: 13, height: 13, borderRadius: 13, justifyContent: 'center', background: consent ? C.emerald : C.line, transition: 'background .2s' }}>
              <Tick on={consent} size={9} />
            </span>
            {consent ? 'Consent given' : 'Not requested'}
          </span>
        </div>
        <div style={{ padding: '8px 13px 4px', borderTop: `1px solid ${C.line}` }}>
          <div className="xp-cap" style={{ fontSize: 7.5, color: C.muted, marginBottom: 2 }}>
            History
          </div>
          {HISTORY.map(([k, d]) => (
            <div key={k} className="xp-row" style={{ justifyContent: 'space-between', height: 21, fontSize: 9.5 }}>
              <span className="xp-row" style={{ gap: 7, whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                <span style={{ width: 5, height: 5, flex: 'none', borderRadius: 5, background: '#B9B7D3' }} />
                {k}
              </span>
              <span className="xp-mono" style={{ fontSize: 8, color: C.muted, whiteSpace: 'nowrap', flex: 'none', marginLeft: 8 }}>
                {d}
              </span>
            </div>
          ))}
          <div style={{ height: at(T.entry) ? 21 : 0, overflow: 'hidden', transition: 'height .35s var(--xe)' }}>
            <div className="xp-row xp-a" style={{ ...rise(at(T.entry), 80, 6), justifyContent: 'space-between', height: 21, fontSize: 9.5, fontWeight: 600, color: C.emerald }}>
              <span className="xp-row" style={{ gap: 7 }}>
                <span style={{ width: 5, height: 5, borderRadius: 5, background: C.emerald }} />
                Ownership transferred
              </span>
              <span className="xp-mono" style={{ fontSize: 8 }}>OCT 2026</span>
            </div>
          </div>
        </div>
        <div className="xp-row" style={{ justifyContent: 'space-between', padding: '8px 13px', borderTop: `1px solid ${C.line}`, background: at(T.kept) ? 'rgba(63,174,108,.08)' : 'transparent', transition: 'background .3s', borderRadius: '0 0 12px 12px' }}>
          <span className="xp-cap" style={{ fontSize: 7.5, color: C.muted }}>
            Visits on record
          </span>
          <span className="xp-row" style={{ gap: 6, fontSize: 10.5, fontWeight: 700 }}>
            {Math.max(HISTORY.length - 1, 0)}
            <span className="xp-mono xp-f" style={{ fontSize: 7.5, fontWeight: 600, color: C.emerald, opacity: at(T.kept) ? 1 : 0 }}>
              KEPT
            </span>
          </span>
        </div>
      </div>
    </>
  );
}

export const transferDef: SceneDef = {
  name: 'Transfer',
  label: (u) => `A ${u.brand} ${u.title} is sold on. ${u.owner2} asks to take over the unit, ${u.owner} gives consent, and the record moves to the new owner with its full history.`,
  W: 480,
  H: 360,
  duration: 8600,
  end: 8000,
  steps: [
    { at: 0, label: 'New owner asks' },
    { at: T.consent, label: 'Consent given' },
    { at: T.move, label: 'Record moves' },
    { at: T.kept, label: 'History kept' },
  ],
  Scene: TransferScene,
};

export const TransferExplainer = (p: ExplainerProps) => <Explainer def={transferDef} {...p} />;
